import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getServiceDependency, getGovernanceMessage, type TransitionPolicy } from '@/lib/serviceDependencyRegistry';

export interface ServiceGovernanceState {
  /** Whether the org has access */
  allowed: boolean;
  /** Loading state */
  isLoading: boolean;
  /** null = allowed; otherwise blocked by global switch, plan, or backend verification */
  blockReason:
    | 'service_globally_disabled'
    | 'plan_not_entitled'
    | 'limit_exhausted'
    | 'wallet_required'
    | 'backend_blocked'
    | null;
  /** True when the service is OFF globally for all orgs */
  isGlobalOff: boolean;
  /** True when the service is OFF for this org's plan */
  isPlanOff: boolean;
  /** Raw entitlement reason, used to distinguish exhausted quota from plan exclusion. */
  entitlementReason: string | null;
  /** User-facing message from dependency registry */
  message: string | null;
  /** The transition policy that should be applied */
  policy: TransitionPolicy | null;
  /** Whether historical data should be shown read-only */
  isReadOnly: boolean;
}

/**
 * Central governance hook that returns full service state —
 * not just a boolean, but the reason, message, and policy.
 *
 * Replaces useServiceAccess for governance-aware components.
 */
export function useServiceGovernance(serviceKey: string): ServiceGovernanceState {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  const { data, isLoading } = useQuery({
    queryKey: ['service-governance', orgId, serviceKey],
    queryFn: async () => {
      const { data: result, error } = await supabase.rpc('check_service_governance', {
        _org_id: orgId!,
        _service_key: serviceKey,
      });

      if (error || !result) {
        return {
          allowed: false,
          blockReason: 'backend_blocked',
          isGlobalOff: false,
          isPlanOff: true,
          entitlementReason: null,
        };
      }

      const r = result as Record<string, unknown>;
      const entitlement = r.entitlement as Record<string, unknown> | null | undefined;
      return {
        allowed: !!r.allowed,
        blockReason: (r.block_reason as string) ?? null,
        isGlobalOff: !!r.is_global_off,
        isPlanOff: !!r.is_plan_off,
        entitlementReason: typeof entitlement?.reason === 'string' ? entitlement.reason : null,
      };
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const blockReason = (data?.blockReason as ServiceGovernanceState['blockReason']) ?? null;
  const dep = getServiceDependency(serviceKey);

  const policy = blockReason
    ? dep
      ? blockReason === 'service_globally_disabled'
        ? dep.globalOffPolicy
        : blockReason === 'backend_blocked'
          ? 'hard_stop'
        : blockReason === 'limit_exhausted' || blockReason === 'wallet_required'
          ? 'hard_stop'
          : dep.planOffPolicy
      : null
    : null;

  return {
    allowed: data?.allowed ?? false,
    isLoading,
    blockReason,
    isGlobalOff: data?.isGlobalOff ?? false,
    isPlanOff: data?.isPlanOff ?? false,
    entitlementReason: data?.entitlementReason ?? null,
    message: blockReason === 'backend_blocked'
      ? 'Subscription access could not be verified. Please try again or contact support.'
      : blockReason === 'limit_exhausted'
        ? 'The plan usage limit for this service has been reached.'
        : blockReason === 'wallet_required'
          ? 'Payment or credits are required before this action can continue.'
          : getGovernanceMessage(serviceKey, blockReason),
    policy,
    isReadOnly: policy === 'read_only' || policy === 'freeze',
  };
}
